/* eslint-disable max-len */
import { useCallback, useContext, useEffect, useState } from 'react';
import { FinancialStatementBasicDataPeriod, FinancialStatementPeriodFullWrite } from 'api';
import { globalAction } from 'common/actions';
import { GET_APPROACHES_ERROR } from 'common/constants/valuations/valuations';
import { useStore } from 'common/store';
import { ValuationsApproach } from 'common/types/valuation';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { CompGroup } from 'pages/CompGroups/components/CompGroups/CompGroups.props';
import { CapIQCompanyData, CapIQPublicCompsList } from 'pages/ValuationsAllocation/types';
import DashboardService from 'services/dashboard';
import ValuationService from 'services/valuations';
import { dbShortDate, getArrayValue, getObjectValue, getStringValue, toString } from 'utilities';
import { useResponse } from './useResponse';

export const useGetValuationApproaches = () => {
  const { processErrorResponse } = useResponse();
  const [, dispatch] = useStore();
  const [data, setData] = useState<ValuationsApproach[]>([]);

  const fetchData = async (measurementDateId: number, allocationVersionId: number, showFeedback = false) => {
    dispatch(globalAction.showLoadingProgress(true));

    try {
      const valuationSvc = new ValuationService();
      const response = (await valuationSvc.getValuationApproaches(
        measurementDateId.toString(),
        allocationVersionId.toString()
      )) as { results: ValuationsApproach[] };

      if (response?.results?.length) {
        setData(response.results);
      }
    } catch (error) {
      setData([]);
      if (showFeedback) {
        processErrorResponse({ error, GET_APPROACHES_ERROR });
      }
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  return [data, fetchData];
};

const useGetGpc = () => {
  const [, dispatch] = useStore();
  const [data, setData] = useState<CapIQCompanyData[] | undefined>();
  const { processErrorResponse } = useResponse();

  const fetch = async (
    ticker: string,
    md: number,
    currency: string,
    marketDate: string,
    financialsPeriods: FinancialStatementPeriodFullWrite[] = [],
    asOfDate?: string
  ) => {
    dispatch(globalAction.showLoadingProgress(true));
    const defaultErrorMessage = `Could not retrieve data for ${ticker}`;
    const errorAction = `get gpc: ${ticker}`;
    try {
      const valuationSvc = new ValuationService();
      const marketDateISO = dbShortDate(marketDate);
      // Generating string with uniques years
      const calendarYears = [...new Set(financialsPeriods.map(period => period.statement_date.slice(0, 4)))].join(',');

      const results: CapIQCompanyData[] = [];
      const gpc = (await valuationSvc.getGpc(ticker, md, currency, calendarYears, marketDateISO!)) as CapIQCompanyData;

      // GPC data is not empty
      if (gpc.valid_gpc_results) {
        results.push(gpc);

        const isFundInvestmentPublicComps = Boolean(asOfDate) && Boolean(marketDate);

        if (isFundInvestmentPublicComps) {
          const asOfDateISO = getStringValue(dbShortDate(asOfDate));

          const gpcAsOfDate = (await valuationSvc.getGpc(
            ticker,
            md,
            currency,
            calendarYears,
            asOfDateISO
          )) as CapIQCompanyData;

          if (gpcAsOfDate.valid_gpc_results) {
            results.push(gpcAsOfDate);
          }
        }
        setData(results);
      } else {
        // The dialog consumes a list in both cases. A bare object leaves its
        // selected-company check empty, hiding feedback and enabling Add.
        setData([{ requested_identifier: ticker, valid_gpc_results: false }]);
      }
    } catch (error) {
      setData(undefined);
      processErrorResponse({
        error,
        defaultErrorMessage,
        action: errorAction,
      });
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  const cleanCompanyData = () => setData(undefined);
  return [data, fetch, cleanCompanyData] as const;
};

const useGetPublicCompList = () => {
  const [, dispatch] = useStore();
  const [data, setData] = useState<CapIQPublicCompsList | undefined>();
  const { processErrorResponse } = useResponse();
  // make sure it recalculates on comps update in backend
  const fetchData = async (
    tickerList: string[],
    md: number,
    currency: string,
    marketDate: string,
    financialsPeriods: FinancialStatementBasicDataPeriod[] = [],
    asOfDate?: string,
    searchForIndexes?: boolean
  ) => {
    dispatch(globalAction.showLoadingProgress(true));
    const defaultErrorMessage = 'An error occurred while retrieving the public companies';
    const valuationService = new ValuationService();
    const calendarYears = [...new Set(financialsPeriods.map(period => period.statement_date.slice(0, 4)))].join(',');
    const marketDateISO = getStringValue(dbShortDate(marketDate));

    const isFundInvestmentPublicComps = Boolean(asOfDate) && Boolean(marketDate);

    try {
      // First fetch for marketDate
      const gpc = (await valuationService.getPublicCompList(
        tickerList,
        `${md}`,
        currency,
        calendarYears,
        marketDateISO,
        searchForIndexes
      )) as CapIQPublicCompsList;

      // For fund investments, retrieve additional GPC data as of asOfDate and combine it with the marketDate results
      if (isFundInvestmentPublicComps) {
        const asOfDateISO = getStringValue(dbShortDate(asOfDate));
        const gpcAsOfDate = (await valuationService.getPublicCompList(
          tickerList,
          `${md}`,
          currency,
          calendarYears,
          asOfDateISO,
          searchForIndexes
        )) as CapIQPublicCompsList;

        const { results = [] } = getObjectValue(gpcAsOfDate);

        if (gpc.results && results.length) {
          gpc.results.push(...results);
        }
      }

      // Filter results to only include companies whose ticker symbols match the requested tickerList.
      // The ticker_symbol.value format is "Exchange:Symbol" (e.g., "NYSE:ACM", "LSE:BBY.L"),
      // so we extract the symbol after the colon and check if it exists in tickerList.
      // Using a Set for O(1) lookup performance instead of O(n) array.includes().
      const tickerSet = new Set(tickerList.map(ticker => decodeURIComponent(ticker)));
      const filteredGpcResults =
        gpc.results?.filter(result => {
          const tickerSymbolValue = toString(result.ticker_symbol?.value || '');
          const symbolAfterColon = tickerSymbolValue.split(':')[1] || '';
          const capIqIdInTickerSet = result.cap_iq_id?.value && tickerSet.has(result.cap_iq_id.value as string);
          return tickerSet.has(symbolAfterColon) || tickerSet.has(tickerSymbolValue) || capIqIdInTickerSet;
        }) ?? [];
      const result: CapIQPublicCompsList = {
        ...gpc,
        results: filteredGpcResults,
      };
      setData(result);
      return result;
    } catch (error) {
      processErrorResponse({
        error,
        defaultErrorMessage,
      });
      return undefined;
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  const removeComps = () => setData(undefined);

  return { data, fetchData, removeComps };
};

export const getCompsListByBatch = async (
  identifiers: string[],
  doCapIqRequest: (chunk: string[]) => Promise<CapIQPublicCompsList>,
  processErrorResponseFn?: (error: any) => void,
  defaultErrorMessage = 'An error occurred while retrieving the public companies'
) => {
  try {
    // split identifiers up into groups of 7, launch a request for each group, and return the combined results
    const MAX_IDENTIFIERS = 7;
    const chunkedIdentifiers = identifiers.reduce((acc, curr, i) => {
      // this is the maximum number of identifiers the backend will process with CAP IQ in a single request
      // so we divide requests into groups of seven for maximum efficiency
      const index = Math.floor(i / MAX_IDENTIFIERS);
      if (!acc[index]) {
        acc[index] = []; // eslint-disable-line no-param-reassign
      }
      acc[index].push(curr);
      return acc;
    }, [] as string[][]);

    const promises = chunkedIdentifiers.map(async chunk => {
      const response = await doCapIqRequest(chunk);
      return response;
    });
    const responses = await Promise.all(promises);
    const results = responses.reduce(
      (acc, curr) => {
        acc.results = [...acc.results, ...curr.results!] as any;
        return acc;
      },
      { results: [] } as { results: CapIQPublicCompsList[] }
    );
    return results;
  } catch (error) {
    const action = 'fetch public comps for date';
    processErrorResponseFn?.({
      error,
      defaultErrorMessage,
      action,
    });
  }
};

const useGetPublicCompListBatchByDate = () => {
  const [, dispatch] = useStore();
  const [data, setData] = useState<{ [key: string]: CapIQPublicCompsList[] } | undefined>();
  const { processErrorResponse } = useResponse();

  const fetchData = async (
    tickerListsByDate: { [key: string]: string[] },
    currency: string,
    financialsPeriods: FinancialStatementPeriodFullWrite[] = []
  ) => {
    const valuationService = new ValuationService();
    const calendarYears = [...new Set(financialsPeriods.map(period => period.statement_date.slice(0, 4)))].join(',');
    const compsByDate: { [key: string]: CapIQPublicCompsList[] } = {};
    dispatch(globalAction.showLoadingProgress(true));

    const promises = Object.entries(tickerListsByDate).map(async ([customDate, tickerList]) => {
      const doCapIqRequest = async (chunk: string[]) => {
        const response = await valuationService.getPublicCompList(chunk, '0', currency, calendarYears, customDate);
        return response as CapIQPublicCompsList;
      };

      const data = await getCompsListByBatch(tickerList, doCapIqRequest, processErrorResponse);
      const { results: comparisons } = data as CapIQPublicCompsList;
      return [customDate, comparisons];
    });

    const results = await Promise.all(promises);
    // eslint-disable-next-line no-restricted-syntax
    for (const [customDate, comparisons] of results) {
      compsByDate[customDate as string] = comparisons as CapIQPublicCompsList[];
    }
    setData(compsByDate);
    dispatch(globalAction.showLoadingProgress(false));
    return compsByDate;
  };

  const removeComps = () => setData(undefined);

  return [data, fetchData, removeComps];
};

const useGetEffectiveTaxRate = () => {
  const { processErrorResponse } = useResponse();

  const fetch = useCallback(
    async companyMeasurementDate => {
      try {
        const valuationSvc = new ValuationService();
        return await valuationSvc.getEffectiveTaxRate(companyMeasurementDate);
      } catch (error) {
        const defaultErrorMessage = 'Effective tax rate could not be found';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'get effective tax rate',
        });
        return error;
      }
    },
    [processErrorResponse]
  );
  return [fetch];
};

const useSaveEffectiveTaxRate = () => {
  const [, dispatch] = useStore();

  const { setAction } = useContext(UnsavedChangesContext);
  const { processErrorResponse, successNotification } = useResponse();

  const saveEffectiveTaxRate = async (data: any) => {
    let response = null;
    if (data) {
      try {
        dispatch(globalAction.showLoadingProgress(true));
        const valuationSvc = new ValuationService();
        response = await valuationSvc.saveEffectiveTaxRate(data);
        setAction(false);
        successNotification('Effective Tax Rate saved successfully');
      } catch (error: any) {
        const defaultErrorMessage = 'An error occurred while saving the Effective Tax Rate';
        processErrorResponse({ error, defaultErrorMessage });
        response = error.response;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    }
    return response;
  };

  return [saveEffectiveTaxRate];
};

/**
 * @function
 * @name useGetCompGroupByFirmId
 * @param {integer} firmId
 * @description Hook that fetch the comp groups list by the firm id
 * @returns {array}
 */
const useGetCompGroupByFirmId = () => {
  const [data, setData] = useState<CompGroup[]>();
  const [{ firmInfo }] = useStore();
  const { processErrorResponse } = useResponse();
  const fetchData = async (firmId: number) => {
    const defaultErrorMessage = 'An error occurred while retrieving the comp groups';
    const valuationService = new ValuationService();
    try {
      const response = (await valuationService.getFirmCompGroups(firmId)) as { results: CompGroup[] };
      const compGroups = getArrayValue(response?.results);

      setData(compGroups);
    } catch (error) {
      processErrorResponse({
        error,
        defaultErrorMessage,
      });
    }
  };
  useEffect(() => {
    if (firmInfo?.id) {
      fetchData(firmInfo?.id);
    }
    // eslint-disable-next-line
  }, [firmInfo]);
  return [data];
};

const useGetCompsByDate = () => {
  const { processErrorResponse } = useResponse();
  const fetch = useCallback(
    async (identifiers: string[], date: string, currency: string, firmId: number, identifiersAsCapIqIds = []) => {
      const service = new DashboardService();
      const doCapIqRequest = async (chunk: string[]): Promise<CapIQPublicCompsList> => {
        const response = await service.getPublicCompsByDate(date, chunk, currency, identifiersAsCapIqIds, `${firmId}`);
        return response as CapIQPublicCompsList;
      };

      const results = await getCompsListByBatch(identifiers, doCapIqRequest, processErrorResponse);
      return results;
    },
    [processErrorResponse]
  );
  return [fetch];
};

const useQueryTransaction = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [, dispatch] = useStore();
  const { processErrorResponse } = useResponse();

  const queryTransaction = useCallback(
    async transactionId => {
      try {
        setIsLoading(true);
        dispatch(globalAction.showLoadingProgress(true));
        const service = new ValuationService();
        const response = await service.getTransaction(transactionId);
        return response;
      } catch (error) {
        const defaultErrorMessage =
          'An error occurred while retrieving the transaction. Check the transaction ID and try again.';
        processErrorResponse({
          error,
          defaultErrorMessage,
        });
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
        setIsLoading(false);
      }
    },
    [processErrorResponse, dispatch]
  );
  return { queryTransaction, isLoading };
};

export {
  useGetCompGroupByFirmId,
  useGetCompsByDate,
  useGetEffectiveTaxRate,
  useGetGpc,
  useGetPublicCompList,
  useGetPublicCompListBatchByDate,
  useQueryTransaction,
  useSaveEffectiveTaxRate,
};
